import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../assets/scss/Lesson.scss";
import Sidebar from "../../components/home/Sidebar.js";
import { useFetchStrapiData } from "../../config/strapiService.js";
import { createSlug, PageSkeleton } from "../../config/utils";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";

const QuizContainer = () => {
	const { quizData, isLoading, error } = useFetchStrapiData();
	const [userAnswers, setUserAnswers] = useState({});
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [showExplanations, setShowExplanations] = useState({});
	const [score, setScore] = useState(0);
	const navigate = useNavigate();

	const { subjectSlug, courseSlug, unitSlug, lessonId } = useParams();

	// Find the current unit and lesson
	const currentUnit = quizData?.units?.find((unit) => unit.slug === unitSlug);
	const currentLesson = currentUnit?.lessons?.find(
		(lesson) => lesson.id === parseInt(lessonId)
	);

	// Loading state
	if (isLoading) {
		return (
			<div className="page-layout">
				<Sidebar />
				<div className="quiz-container">
					return <PageSkeleton />;
				</div>
			</div>
		);
	}

	// Error state
	if (error) {
		return (
			<div className="page-layout">
				<Sidebar />
				<div className="quiz-container">
					<div className="error-container">
						<p className="error-message">{error}</p>
					</div>
				</div>
			</div>
		);
	}

	// No data state
	if (
		!quizData ||
		!currentLesson?.questions ||
		currentLesson?.questions === null
	) {
		return (
			<div className="page-layout">
				<Sidebar />
				<div className="quiz-container">
					<p>No quiz available.</p>
				</div>
			</div>
		);
	}

	const questions = currentLesson?.questions || [];

	const handleAnswerSelect = (questionId, selectedAnswer) => {
		setUserAnswers((prev) => ({
			...prev,
			[questionId]: selectedAnswer,
		}));
	};

	const handleSubmit = (questionId) => {
		setIsSubmitted((prev) => ({
			...prev,
			[questionId]: true,
		}));
		setScore(calculateScore().percentage);
	};

	const toggleExplanation = (questionId) => {
		setShowExplanations((prev) => ({
			...prev,
			[questionId]: !prev[questionId],
		}));
	};

	const calculateScore = () => {
		let correct = 0;
		questions.forEach((question) => {
			if (userAnswers[question.id] === question.correctAnswer) {
				correct++;
			}
		});
		return {
			score: correct,
			total: questions.length,
			percentage: Math.round((correct / questions.length) * 100),
		};
	};

	// Handle next lesson navigation
	const handleNextLesson = () => {
		if (!currentUnit?.lessons) {
			console.error("No lessons found in current unit");
			return;
		}

		// Get current lesson index in the unit
		const currentIndex = currentUnit.lessons.findIndex(
			(lesson) => lesson.id === parseInt(lessonId)
		);

		// If there's a next lesson in the same unit
		if (currentIndex < currentUnit.lessons.length - 1) {
			const nextLesson = currentUnit.lessons[currentIndex + 1];
			navigate(
				`/courses/${subjectSlug}/${courseSlug}/${unitSlug}/${createSlug(nextLesson.title)}/${nextLesson.id}`
			);
		} else {
			// If we're at the last lesson of the current unit, find the next unit
			const unitIndex = quizData.units.findIndex(
				(unit) => unit.id === currentUnit.id
			);
			if (unitIndex < quizData.units.length - 1) {
				const nextUnit = quizData.units[unitIndex + 1];
				const firstLessonOfNextUnit = nextUnit.lessons[0];
				navigate(
					`/courses/${subjectSlug}/${courseSlug}/${createSlug(nextUnit.title)}/${createSlug(firstLessonOfNextUnit.title)}/${firstLessonOfNextUnit.id}`
				);
			}
		}
	};

	const handleRetry = () => {
		setUserAnswers({});
		setIsSubmitted(false);
		setScore(0);
	};

	const renderMixedMath = (text) => {
		if (!text) return null;

		// Split text into LaTeX and non-LaTeX parts
		// This regex now handles more complex LaTeX expressions
		const parts = text.split(/(\$\$.*?\$\$|\$.*?\$)/gs);

		return parts.map((part, i) => {
			// Handle display math ($$...$$)
			if (part.startsWith("$$") && part.endsWith("$$")) {
				// Remove the $$ delimiters and clean up any extra whitespace
				const mathContent = part.slice(2, -2).trim();
				return <BlockMath key={i} math={mathContent} />;
			}
			// Handle inline math ($...$)
			else if (part.startsWith("$") && part.endsWith("$")) {
				// Remove the $ delimiters and clean up any extra whitespace
				const mathContent = part.slice(1, -1).trim();
				return <InlineMath key={i} math={mathContent} />;
			}
			// Handle regular text
			else {
				// Replace common LaTeX-style line breaks with proper spacing
				const formattedText = part
					.replace(/\\quad/g, " ")
					.replace(/\\[\\,]/g, " ")
					.replace(/\\text\{([^}]*)\}/g, "$1");

				return <span key={i}>{formattedText}</span>;
			}
		});
	};

	const ScrollToTop = () => {
		const [isVisible, setIsVisible] = useState(false);

		// Show button when page is scrolled up to given distance
		const toggleVisibility = () => {
			if (window.pageYOffset > 300) {
				setIsVisible(true);
			} else {
				setIsVisible(false);
			}
		};

		// Set the scroll event listener when the component mounts
		useEffect(() => {
			window.addEventListener("scroll", toggleVisibility);
			return () => {
				window.removeEventListener("scroll", toggleVisibility);
			};
		}, []);

		const scrollToTop = () => {
			window.scrollTo({
				top: 0,
				behavior: "smooth",
			});
		};

		return (
			<>
				{isVisible && (
					<button
						onClick={scrollToTop}
						className="scroll-to-top"
						aria-label="Scroll to top">
						Scroll ↑
					</button>
				)}
			</>
		);
	};

	return (
		<div className="page-layout">
			<Sidebar />
			<div className="quiz-container">
				<h1 className="quiz-container__title">
					{currentLesson?.title} Quiz
				</h1>
				<div className="quiz-container__questions">
					{questions?.map((question, index) => (
						<div key={question.id} className="quiz-question">
							<h2 className="quiz-question__title">
								{index + 1}. {renderMixedMath(question.title)}
							</h2>
							<div className="quiz-question__options">
								{question.options.map((option) => (
									<div
										key={option.label}
										className="quiz-question__option-wrapper">
										<label className="quiz-question__option">
											<input
												type="radio"
												name={`question-${question.id}`}
												value={option.label}
												checked={
													userAnswers[question.id] ===
													option.label
												}
												onChange={() =>
													handleAnswerSelect(
														question.id,
														option.label
													)
												}
												disabled={isSubmitted}
											/>
											<span
												className={`quiz-question__option-circle ${
													isSubmitted &&
													option.label ===
														question.correctAnswer
														? "quiz-question__option-circle--correct"
														: isSubmitted &&
															  userAnswers[
																	question.id
															  ] ===
																	option.label &&
															  option.label !==
																	question.correctAnswer
															? "quiz-question__option-circle--incorrect"
															: ""
												}`}>
												{option.label}
											</span>
											<span className="quiz-question__option-text">
												{renderMixedMath(option.answer)}
											</span>
										</label>
									</div>
								))}
							</div>

							{isSubmitted && (
								<div className="quiz-question__feedback">
									{userAnswers[question.id] ===
									question.correctAnswer ? (
										<div className="quiz-question__feedback--correct">
											<p className="feedback-result">
												Correct!
											</p>
											<button
												className="explanation-toggle"
												onClick={() =>
													toggleExplanation(
														question.id
													)
												}
												aria-expanded={
													showExplanations[
														question.id
													]
												}
												aria-controls={`explanation-${question.id}`}>
												{showExplanations[question.id]
													? "Hide"
													: "See"}{" "}
												Full Explanation
											</button>

											<div
												id={`explanation-${question.id}`}
												className={`full-explanation ${showExplanations[question.id] ? "expanded" : ""}`}
												role="region"
												aria-hidden={
													showExplanations[
														question.id
													]
												}>
												<div className="math-content">
													{renderMixedMath(
														question.options.find(
															(opt) =>
																opt.label ===
																question.correctAnswer
														)?.explanation ||
															"No explanation available."
													)}
												</div>
											</div>
										</div>
									) : (
										<div className="quiz-question__feedback--incorrect">
											<div className="incorrect-explanation">
												<p className="feedback-result">
													Incorrect
												</p>
												<p>
													<strong>
														Explanation:
													</strong>
												</p>
												<div className="math-content">
													<p>
														{renderMixedMath(
															question.options.find(
																(opt) =>
																	opt.label ===
																	userAnswers[
																		question
																			.id
																	]
															)?.explanation || ""
														)}
													</p>
												</div>
											</div>

											<div className="correct-answer">
												<p>
													<strong>
														Correct Answer:{" "}
														{question.correctAnswer}
													</strong>
												</p>
												<p>
													{renderMixedMath(
														question.options.find(
															(opt) =>
																opt.label ===
																question.correctAnswer
														)?.answer || ""
													)}
												</p>
											</div>

											<button
												className="explanation-toggle"
												onClick={() =>
													toggleExplanation(
														question.id
													)
												}
												aria-expanded={
													showExplanations[
														question.id
													]
												}
												aria-controls={`explanation-${question.id}`}>
												{showExplanations[question.id]
													? "Hide"
													: "See"}{" "}
												Full Explanation
											</button>

											{showExplanations[question.id] && (
												<div
													id={`explanation-${question.id}`}
													className={`full-explanation ${showExplanations[question.id] ? "expanded" : ""}`}
													role="region"
													aria-hidden={
														!showExplanations[
															question.id
														]
													}>
													<div className="math-content">
														{renderMixedMath(
															question.options.find(
																(opt) =>
																	opt.label ===
																	question.correctAnswer
															)?.explanation ||
																"No explanation available."
														)}
													</div>
												</div>
											)}
										</div>
									)}
								</div>
							)}
						</div>
					))}
				</div>

				<div className="quiz-container__actions">
					<ScrollToTop />
					{isSubmitted ? (
						<>
							<div className="quiz-container__score">
								<strong>Score:</strong> {calculateScore().score}
								/{calculateScore().total} = {score}%
							</div>
							<div className="quiz-container__buttons">
								<button
									className="retry-btn"
									onClick={handleRetry}>
									Retry Quiz
								</button>
								<button
									className="next-btn"
									onClick={handleNextLesson}>
									Next Lesson
								</button>
							</div>
						</>
					) : (
						<button
							className="submit-btn"
							onClick={handleSubmit}
							disabled={
								Object.keys(userAnswers).length !==
								questions?.length
							}>
							Submit Quiz
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

export default QuizContainer;
